/**
 * Housing Calculator - Rent vs Buy engine
 *
 * Pure functions, no React. HousingCalculator.tsx resolves UI-only concerns (itemised closing
 * costs, first-time-buyer rebates) and hands the numbers here.
 *
 * Model (same as PWL Capital's rent-vs-own calculator):
 *   - Owner pays down payment, then monthly mortgage + tax + maintenance + insurance + condo fees.
 *   - Renter invests the down payment, receives the owner's full monthly outlay as
 *     "cash available", pays rent out of it, and invests whatever is left.
 *     When rent exceeds the owner's outlay the fund is drawn down (negative saving).
 *   - Equivalent rent = the rent at which the renter's final fund equals the owner's
 *     net sale proceeds. Rent below that means renting wins; above, buying wins.
 *   - Investment gains are taxed once, on the way out, the same way the owner's gain is. Nothing
 *     is withheld as the fund grows: the renter is not handing the CRA a cheque every month.
 */

import {
  HousingComparisonData,
  MonthlyCashFlow,
  RentalFundFlow,
  CostBreakdown,
  PurchaseSummary,
  RentalAnalysis
} from '../../types/housingComparison';
import { calculateMonthlyMortgagePayment, calculateMortgageInsurance } from './housingCalculations';

export interface OwnerCashFlowParams {
  houseCost: number;
  ownershipYears: number;
  mortgagePrincipal: number;
  monthlyMortgagePayment: number;
  annualInterestRate: number;
  monthlyMortgageInsurance: number;
  region: 'Canada' | 'US';
  costInflationRate: number;
  propertyTaxPercent: number;
  maintenancePercent: number;
  annualHomeInsurance: number;
  condoFees: number;
}

/**
 * Month-by-month owner cash flows for the ownership period.
 * Mortgage payments stop once the balance reaches zero; the final payment is
 * only what is left owing plus that month's interest.
 */
export const buildOwnerMonthlyCashFlows = (params: OwnerCashFlowParams): MonthlyCashFlow[] => {
  const cashFlows: MonthlyCashFlow[] = [];
  const monthlyRate = params.annualInterestRate / 100 / 12;
  const annualInflation = params.costInflationRate / 100;
  const monthlyHomeInsurance = params.annualHomeInsurance / 12;
  const monthlyPropertyTax = (params.houseCost * params.propertyTaxPercent / 100) / 12;
  const monthlyMaintenance = (params.houseCost * params.maintenancePercent / 100) / 12;
  let remainingBalance = params.mortgagePrincipal;
  let cumulativeExpense = 0;

  for (let month = 1; month <= params.ownershipYears * 12; month++) {
    const year = Math.floor((month - 1) / 12) + 1;

    const interestPaid = remainingBalance * monthlyRate;
    const isFinalPayment = remainingBalance + interestPaid <= params.monthlyMortgagePayment;
    let mortgagePayment = params.monthlyMortgagePayment;
    if (remainingBalance <= 0) {
      mortgagePayment = 0;
    } else if (isFinalPayment) {
      mortgagePayment = remainingBalance + interestPaid;
    }
    const principalPaid = mortgagePayment - interestPaid;
    remainingBalance = Math.max(0, remainingBalance - principalPaid);

    // PMI drops off when loan-to-value reaches 80% (USA only)
    const currentLTV = (remainingBalance / params.houseCost) * 100;
    const mortgageInsurance = (params.region === 'US' && currentLTV > 80) ? params.monthlyMortgageInsurance : 0;

    const inflationFactor = Math.pow(1 + annualInflation, year - 1);
    const propertyTax = monthlyPropertyTax * inflationFactor;
    const maintenance = monthlyMaintenance * inflationFactor;
    const homeInsurance = monthlyHomeInsurance * inflationFactor;
    const condoFees = params.condoFees * inflationFactor;
    const totalMonthlyExpense = mortgagePayment + propertyTax + maintenance + mortgageInsurance + homeInsurance + condoFees;
    cumulativeExpense += totalMonthlyExpense;

    cashFlows.push({
      month,
      year,
      mortgagePayment,
      propertyTax,
      homeInsurance,
      mortgageInsurance,
      maintenance,
      condoFees,
      totalMonthlyExpense,
      principalPaid,
      interestPaid,
      remainingBalance,
      cumulativeExpense
    });
  }

  return cashFlows;
};

export interface RenterFundParams {
  initialFunding: number;
  ownerMonthlyExpenses: number[];
  monthlyRent: number;
  rentInflationRate: number;
  investmentReturnRate: number;
  investmentTaxRate: number;
  /** Share of each month's saving the renter actually invests; the rest is spent. Unset means 100. */
  savingsDisciplinePercent?: number;
}

export interface RenterFundResult {
  rentalFundFlows: RentalFundFlow[];
  totalRentalPayments: number;
  totalInvestmentIncome: number;
  /** Tax on the fund's gain, paid once when it is cashed out at the end. */
  capitalGainsTax: number;
  /** Net of that tax, so it compares like for like with the owner's net sale proceeds. */
  finalFundBalance: number;
}

/**
 * Tax owed if the fund were cashed out right now. Money paid in and taken out moves the cost base;
 * growth does not, so growth is only taxed when it is realised. Withdrawals are treated as a return
 * of capital first, which is the simple convention and the one that favours neither side much.
 */
const taxOnGain = (fundBalance: number, costBasis: number, taxRate: number): number =>
  Math.max(0, fundBalance - costBasis) * taxRate;

/** Rent due in a given month. First month pays first + last; last month pays nothing. */
const rentForMonth = (baseRent: number, month: number, totalMonths: number, rentInflationRate: number): number => {
  const yearIndex = Math.floor((month - 1) / 12);
  const inflatedRent = baseRent * Math.pow(1 + rentInflationRate / 100, yearIndex);
  if (month === 1) {
    return inflatedRent * 2;
  }
  if (month === totalMonths) {
    return 0;
  }
  return inflatedRent;
};

/**
 * What the renter puts into the fund this month. They always cover the rent; of any saving left
 * over (owner's cost minus rent) they invest only their discipline share and spend the rest.
 * When rent exceeds the owner's cost the shortfall comes out of the fund, discipline or not.
 */
const renterMonthlyFunding = (ownerExpense: number, rent: number, savingsDisciplinePercent: number): number => {
  const saving = ownerExpense - rent;
  if (saving <= 0) {
    return ownerExpense;
  }
  return rent + saving * savingsDisciplinePercent / 100;
};

/** Final fund balance only — no per-month records, used by the rent search. */
const finalFundBalanceForRent = (params: RenterFundParams): number => {
  const months = simulateInvestmentFund({
    initialFunding: params.initialFunding,
    netContributions: renterNetContributions(params),
    investmentReturnRate: params.investmentReturnRate,
    investmentTaxRate: params.investmentTaxRate
  });
  return months[months.length - 1]?.closingBalanceAfterTax ?? 0;
};

/** What the renter puts in each month, net of the rent they take back out to pay the landlord. */
const renterNetContributions = (params: RenterFundParams): number[] => {
  const totalMonths = params.ownerMonthlyExpenses.length;
  const discipline = params.savingsDisciplinePercent ?? 100;
  const contributions: number[] = [];

  for (let month = 1; month <= totalMonths; month++) {
    const rent = rentForMonth(params.monthlyRent, month, totalMonths, params.rentInflationRate);
    contributions.push(renterMonthlyFunding(params.ownerMonthlyExpenses[month - 1], rent, discipline) - rent);
  }

  return contributions;
};

/** Full month-by-month renter fund simulation at a fixed rent. */
export const simulateRenterFund = (params: RenterFundParams): RenterFundResult => {
  const totalMonths = params.ownerMonthlyExpenses.length;
  const discipline = params.savingsDisciplinePercent ?? 100;
  const months = simulateInvestmentFund({
    initialFunding: params.initialFunding,
    netContributions: renterNetContributions(params),
    investmentReturnRate: params.investmentReturnRate,
    investmentTaxRate: params.investmentTaxRate
  });

  let totalRentalPayments = 0;
  let totalInvestmentIncome = 0;

  const rentalFundFlows: RentalFundFlow[] = months.map((fundMonth, index) => {
    const month = index + 1;
    const rentalPayment = rentForMonth(params.monthlyRent, month, totalMonths, params.rentInflationRate);
    const monthlyFunding = renterMonthlyFunding(params.ownerMonthlyExpenses[index], rentalPayment, discipline);

    totalRentalPayments += rentalPayment;
    totalInvestmentIncome += fundMonth.investmentIncome;

    return {
      month,
      year: Math.floor(index / 12) + 1,
      openingBalance: fundMonth.openingBalance,
      initialFunding: month === 1 ? params.initialFunding : 0,
      monthlyFunding,
      rentalPayment,
      investmentIncome: fundMonth.investmentIncome,
      taxes: 0,
      closingBalance: fundMonth.closingBalance,
      closingBalanceAfterTax: fundMonth.closingBalanceAfterTax
    };
  });

  const finalMonth = months[months.length - 1];
  return {
    rentalFundFlows,
    totalRentalPayments,
    totalInvestmentIncome,
    capitalGainsTax: finalMonth ? finalMonth.taxIfSoldNow : 0,
    finalFundBalance: finalMonth ? finalMonth.closingBalanceAfterTax : 0
  };
};

export interface InvestmentFundMonth {
  openingBalance: number;
  /** Money moving in (positive) or out (negative) of the fund this month, before growth. */
  netContribution: number;
  investmentIncome: number;
  closingBalance: number;
  /** What has been paid in, less what has been taken out. Growth never moves it. */
  costBasis: number;
  /** Capital gains tax that selling this month would trigger. */
  taxIfSoldNow: number;
  closingBalanceAfterTax: number;
}

export interface InvestmentFundParams {
  initialFunding: number;
  /** One entry per month. Positive adds to the fund, negative draws from it. */
  netContributions: number[];
  investmentReturnRate: number;
  investmentTaxRate: number;
}

/**
 * The one investment fund model. Both sides of the calculator run through this: the renter
 * investing what they save against an owner's costs, and the would-be landlord investing what the
 * property would have tied up. Growth compounds untaxed and is taxed once on the way out, against
 * a cost base that only money in and out can move.
 *
 * Keeping this in one place is deliberate — when the landlord comparison had its own copy, the
 * break-even search and the panel that displayed the result drifted apart and stopped agreeing.
 */
export const simulateInvestmentFund = (params: InvestmentFundParams): InvestmentFundMonth[] => {
  const monthlyReturn = params.investmentReturnRate / 100 / 12;
  const taxRate = params.investmentTaxRate / 100;
  const months: InvestmentFundMonth[] = [];
  let fundBalance = 0;
  let costBasis = 0;

  params.netContributions.forEach((contribution, index) => {
    const openingBalance = fundBalance;
    const netContribution = (index === 0 ? params.initialFunding : 0) + contribution;
    const investmentIncome = openingBalance * monthlyReturn;

    fundBalance = openingBalance + netContribution + investmentIncome;
    costBasis = Math.max(0, costBasis + netContribution);
    const taxIfSoldNow = taxOnGain(fundBalance, costBasis, taxRate);

    months.push({
      openingBalance,
      netContribution,
      investmentIncome,
      closingBalance: fundBalance,
      costBasis,
      taxIfSoldNow,
      closingBalanceAfterTax: fundBalance - taxIfSoldNow
    });
  });

  return months;
};

export interface EquivalentRentParams extends Omit<RenterFundParams, 'monthlyRent'> {
  targetFundBalance: number;
}

export interface EquivalentRentResult {
  rent: number;
  iterations: number;
}

/**
 * Find the monthly rent at which the renter's final fund equals targetFundBalance
 * (the owner's net sale proceeds). Step sizes shrink $100 -> $10 -> $1 -> $0.10 -> $0.01
 * until the fund is within $1 of the target.
 */
export const findEquivalentRent = (params: EquivalentRentParams): EquivalentRentResult => {
  const balanceGap = (rent: number) => finalFundBalanceForRent({ ...params, monthlyRent: rent }) - params.targetFundBalance;
  let rent = 3000;
  let iterations = 0;

  // Coarse-to-fine: step until the gap changes sign, then back up one step and refine.
  const phases: Array<{ step: number; maxIterations: number }> = [
    { step: 100, maxIterations: 500 },
    { step: 10, maxIterations: 1000 },
    { step: 1, maxIterations: 1000 },
    { step: 0.10, maxIterations: 1000 }
  ];

  for (const phase of phases) {
    let lastGap = balanceGap(rent);
    let lastRent = rent;
    while (iterations < phase.maxIterations) {
      iterations++;
      // Fund above target means rent can go higher; below target means lower.
      rent += lastGap > 0 ? phase.step : -phase.step;
      const newGap = balanceGap(rent);
      if ((lastGap > 0) !== (newGap > 0)) {
        rent = lastRent;
        break;
      }
      lastGap = newGap;
      lastRent = rent;
    }
  }

  // Final pass: $0.01 steps until within $1, stop if we start oscillating.
  const finalStep = 0.01;
  let gap = balanceGap(rent);
  while (Math.abs(gap) >= 1 && iterations < 1000) {
    iterations++;
    const direction = gap > 0 ? finalStep : -finalStep;
    rent += direction;
    const newGap = balanceGap(rent);
    if (Math.abs(newGap) > Math.abs(gap)) {
      rent -= direction;
      break;
    }
    gap = newGap;
  }

  return { rent, iterations };
};

/**
 * A future dollar amount expressed in today's money. A $1M home value in 25 years sounds like a
 * win until you notice it buys what $478K buys now, so the headline figures carry both.
 */
export const discountToTodaysDollars = (amount: number, years: number, annualInflationRate: number): number => {
  if (years <= 0 || annualInflationRate <= 0) {
    return amount;
  }
  return amount / Math.pow(1 + annualInflationRate / 100, years);
};

/**
 * The share of the monthly saving a renter has to actually invest for renting to finish level with
 * buying. The renter's fund only grows with discipline, so this is the floor: below it, buying wins.
 *
 * null when discipline cannot decide the question — renting already wins with the renter investing
 * none of the monthly saving, or buying still wins with the renter investing every dollar of it.
 */
export const findBreakEvenSavingsDiscipline = (
  params: Omit<RenterFundParams, 'savingsDisciplinePercent'>,
  targetFundBalance: number
): number | null => {
  const fundAt = (savingsDisciplinePercent: number) =>
    finalFundBalanceForRent({ ...params, savingsDisciplinePercent });

  if (fundAt(0) >= targetFundBalance || fundAt(100) < targetFundBalance) {
    return null;
  }

  let needsMore = 0;
  let enough = 100;
  for (let iteration = 0; iteration < 24; iteration++) {
    const middle = (needsMore + enough) / 2;
    if (fundAt(middle) < targetFundBalance) {
      needsMore = middle;
    } else {
      enough = middle;
    }
  }

  return Math.ceil(enough);
};

export interface CostBreakdownParams {
  monthlyCashFlows: MonthlyCashFlow[];
  sellingCosts: number;
  capitalGain: number;
  capitalGainsTax: number;
  /** Capitalised CMHC premium. It is financed rather than paid monthly, so nothing else catches it. */
  mortgageInsurancePremium: number;
}

/**
 * Where the owner's money went over the period. `total` is every cost including
 * capital gains tax; `netCost` is total minus the appreciation gain.
 */
export const buildCostBreakdown = (params: CostBreakdownParams): CostBreakdown => {
  const sum = (pick: (flow: MonthlyCashFlow) => number) =>
    params.monthlyCashFlows.reduce((runningTotal, flow) => runningTotal + pick(flow), 0);

  const interest = sum(flow => flow.interestPaid);
  const propertyTax = sum(flow => flow.propertyTax);
  const maintenance = sum(flow => flow.maintenance);
  const condoFees = sum(flow => flow.condoFees);
  const insurance = sum(flow => flow.homeInsurance) + sum(flow => flow.mortgageInsurance);
  const total = interest + propertyTax + maintenance + condoFees + insurance
    + params.mortgageInsurancePremium + params.sellingCosts + params.capitalGainsTax;

  return {
    interest,
    propertyTax,
    maintenance,
    condoFees,
    insurance,
    mortgageInsurancePremium: params.mortgageInsurancePremium,
    sellingCosts: params.sellingCosts,
    capitalGains: -(params.capitalGain - params.capitalGainsTax),
    netCost: total - params.capitalGain,
    total
  };
};

/**
 * Tax on the gain from a sale. Selling costs (commission, legal) reduce the taxable gain in both
 * Canada (outlays and expenses of disposition) and the US (amount realized is net of selling expenses).
 */
export const calculateCapitalGainsTax = (salePrice: number, sellingCosts: number, adjustedCostBase: number, taxesPercent: number): number => {
  const taxableGain = salePrice - sellingCosts - adjustedCostBase;
  return taxableGain > 0 ? taxableGain * (taxesPercent / 100) : 0;
};

/** Down payment in dollars, whichever way the user entered it. */
export const resolveDownpayment = (inputs: HousingComparisonData): number =>
  inputs.useDownpaymentPercent
    ? inputs.houseCost * inputs.downpaymentPercent / 100
    : inputs.downpaymentAmount;

const EMPTY_COST_BREAKDOWN: CostBreakdown = {
  interest: 0, propertyTax: 0, maintenance: 0, condoFees: 0, insurance: 0,
  mortgageInsurancePremium: 0, sellingCosts: 0, capitalGains: 0, netCost: 0, total: 0
};

/**
 * The buy side end to end: financing, month-by-month cash flows, sale proceeds, cost breakdown.
 * closingCosts is supplied by the caller because the UI may use an itemised list or a
 * first-time-buyer rebate. Closing costs are paid in cash; the renter invests them with the down payment.
 */
export const calculateOwnerSummary = (inputs: HousingComparisonData, closingCosts: number): PurchaseSummary => {
  if (inputs.ownershipYears <= 0 || inputs.houseCost <= 0) {
    return {
      totalFinancingRequired: 0, downpayment: 0, closingCosts: 0, totalMortgageValue: 0, monthlyMortgagePayment: 0, totalMonthlyExpense: 0,
      totalExpensesOverPeriod: 0, estimatedSalePrice: 0, sellingCosts: 0, taxes: 0, netProceedsFromSale: 0,
      remainingMortgageBalance: 0, netCashFlow: 0, totalInvestment: 0, monthlyCost: 0, monthlyCashFlows: [],
      costBreakdown: { ...EMPTY_COST_BREAKDOWN }
    };
  }

  const totalFinancingRequired = inputs.houseCost + closingCosts;
  const downpayment = resolveDownpayment(inputs);
  const downpaymentPercent = (downpayment / inputs.houseCost) * 100;
  // Only the house is financed; closing costs are paid in cash at purchase
  const baseLoanAmount = Math.max(0, inputs.houseCost - downpayment);

  // Canada (CMHC): one-time premium added to the mortgage. USA (PMI): paid monthly until 80% LTV.
  const mortgageInsurance = calculateMortgageInsurance(baseLoanAmount, downpaymentPercent, inputs.region, inputs.mortgageInsuranceRate);
  const totalMortgageValue = inputs.region === 'Canada'
    ? baseLoanAmount + mortgageInsurance.upfrontPremium
    : baseLoanAmount;
  const monthlyMortgagePayment = calculateMonthlyMortgagePayment(totalMortgageValue, inputs.interestRate, inputs.mortgageYears);

  // Landlord mode keeps owner costs tied to rent inflation so a landlord can't profit from an inflation spread.
  // The rent-vs-buy modes let rent and owner costs inflate at their own rates (rent often runs above CPI).
  const costInflationRate = inputs.analysisMode === 'landlord-breakeven'
    ? inputs.rentalInflationRate
    : inputs.inflationRate;

  const monthlyCashFlows = buildOwnerMonthlyCashFlows({
    houseCost: inputs.houseCost,
    ownershipYears: inputs.ownershipYears,
    mortgagePrincipal: totalMortgageValue,
    monthlyMortgagePayment,
    annualInterestRate: inputs.interestRate,
    monthlyMortgageInsurance: mortgageInsurance.monthlyPremium,
    region: inputs.region,
    costInflationRate,
    propertyTaxPercent: inputs.propertyTaxPercent,
    maintenancePercent: inputs.maintenancePercent,
    annualHomeInsurance: inputs.annualHomeInsurance || 0,
    condoFees: inputs.condoFees
  });
  const lastMonth = monthlyCashFlows[monthlyCashFlows.length - 1];
  const totalExpensesOverPeriod = lastMonth.cumulativeExpense;
  const remainingMortgageBalance = lastMonth.remainingBalance;

  const estimatedSalePrice = inputs.houseCost * Math.pow(1 + inputs.annualAppreciation / 100, inputs.ownershipYears);
  const sellingCosts = estimatedSalePrice * inputs.sellingCostsPercent / 100;
  const adjustedCostBase = inputs.houseCost + closingCosts;
  // capitalGain is the appreciation shown in the cost breakdown; the taxable gain is also net of selling costs
  const capitalGain = estimatedSalePrice - adjustedCostBase;
  const capitalGainsTax = calculateCapitalGainsTax(estimatedSalePrice, sellingCosts, adjustedCostBase, inputs.taxesPercent);
  const netProceedsFromSale = estimatedSalePrice - sellingCosts - capitalGainsTax - remainingMortgageBalance;

  const costBreakdown = buildCostBreakdown({
    monthlyCashFlows,
    sellingCosts,
    capitalGain,
    capitalGainsTax,
    mortgageInsurancePremium: inputs.region === 'Canada' ? mortgageInsurance.upfrontPremium : 0
  });
  const totalMonths = inputs.ownershipYears * 12;

  return {
    totalFinancingRequired,
    downpayment,
    closingCosts,
    totalMortgageValue,
    monthlyMortgagePayment,
    totalMonthlyExpense: monthlyCashFlows[0].totalMonthlyExpense,
    totalExpensesOverPeriod,
    estimatedSalePrice,
    sellingCosts,
    taxes: capitalGainsTax,
    netProceedsFromSale,
    remainingMortgageBalance,
    netCashFlow: netProceedsFromSale,
    totalInvestment: downpayment + closingCosts + totalExpensesOverPeriod,
    monthlyCost: costBreakdown.netCost / totalMonths,
    monthlyCashFlows,
    costBreakdown
  };
};

export interface RenterSummary extends RentalAnalysis {
  iterations: number;
}

/**
 * The rent side. With useInputRent the renter pays inputs.monthlyRent; otherwise we solve
 * for the rent that leaves the renter exactly as wealthy as the owner (equivalent rent).
 */
export const calculateRenterSummary = (
  inputs: HousingComparisonData,
  owner: PurchaseSummary,
  useInputRent = false
): RenterSummary => {
  const fundParams = {
    initialFunding: owner.downpayment + owner.closingCosts,
    ownerMonthlyExpenses: owner.monthlyCashFlows.map(flow => flow.totalMonthlyExpense),
    rentInflationRate: inputs.rentalInflationRate,
    investmentReturnRate: inputs.investmentReturnRate,
    investmentTaxRate: inputs.investmentTaxRate,
    savingsDisciplinePercent: inputs.renterSavingsDisciplinePercent ?? 100
  };

  const search = useInputRent
    ? { rent: inputs.monthlyRent, iterations: 0 }
    : findEquivalentRent({ ...fundParams, targetFundBalance: owner.netProceedsFromSale });

  const fund = simulateRenterFund({ ...fundParams, monthlyRent: search.rent });
  return { equivalentMonthlyRent: search.rent, iterations: search.iterations, ...fund };
};

export interface NetWorthByYear {
  year: number;
  ownerNetWorth: number;
  renterNetWorth: number;
}

/**
 * Wealth of each side at the end of every year. Owner: what they would walk away with if they
 * sold that year (value less selling costs, capital gains tax and mortgage balance). Renter: the
 * investment fund. Pass the renter simulation run at the rent the user actually pays.
 */
export const buildNetWorthByYear = (
  inputs: HousingComparisonData,
  owner: PurchaseSummary,
  renter: RentalAnalysis
): NetWorthByYear[] => {
  const series: NetWorthByYear[] = [];
  const adjustedCostBase = inputs.houseCost + owner.closingCosts;

  for (let year = 1; year <= inputs.ownershipYears; year++) {
    const lastMonthIndex = year * 12 - 1;
    const homeValue = inputs.houseCost * Math.pow(1 + inputs.annualAppreciation / 100, year);
    const sellingCosts = homeValue * inputs.sellingCostsPercent / 100;
    const capitalGainsTax = calculateCapitalGainsTax(homeValue, sellingCosts, adjustedCostBase, inputs.taxesPercent);
    const mortgageBalance = owner.monthlyCashFlows[lastMonthIndex]?.remainingBalance ?? 0;

    series.push({
      year,
      ownerNetWorth: homeValue - sellingCosts - capitalGainsTax - mortgageBalance,
      renterNetWorth: renter.rentalFundFlows[lastMonthIndex]?.closingBalanceAfterTax
        ?? renter.rentalFundFlows[lastMonthIndex]?.closingBalance ?? 0
    });
  }

  return series;
};

export interface Crossover {
  winner: 'buy' | 'rent' | 'tie';
  leadFromYear: number;
}

/**
 * The hold period at which buying starts to pay: the first year from which the buyer is ahead of
 * the renter and stays ahead for the rest of the series. null when the buyer never ends ahead.
 *
 * Unlike findCrossover this is always about the buyer, so it answers the same question whichever
 * side is winning: "how long would I have to stay for buying to be the better call?"
 */
export const findBuyerBreakEvenYear = (series: NetWorthByYear[]): number | null => {
  if (series.length === 0) {
    return null;
  }

  const final = series[series.length - 1];
  if (final.ownerNetWorth <= final.renterNetWorth) {
    return null;
  }

  let breakEvenYear = series[0].year;
  for (const point of series) {
    if (point.ownerNetWorth < point.renterNetWorth) {
      breakEvenYear = point.year + 1;
    }
  }

  return breakEvenYear;
};

/** Who ends ahead, and the first year from which they stay ahead. leadFromYear 1 means the whole way. */
export const findCrossover = (series: NetWorthByYear[]): Crossover => {
  if (series.length === 0) {
    return { winner: 'tie', leadFromYear: 1 };
  }

  const final = series[series.length - 1];
  const finalGap = final.ownerNetWorth - final.renterNetWorth;
  if (Math.abs(finalGap) < 1) {
    return { winner: 'tie', leadFromYear: 1 };
  }

  const winner = finalGap > 0 ? 'buy' : 'rent';
  const winnerIsBehind = (point: NetWorthByYear) => (winner === 'buy'
    ? point.ownerNetWorth < point.renterNetWorth
    : point.renterNetWorth < point.ownerNetWorth);

  let leadFromYear = 1;
  for (const point of series) {
    if (winnerIsBehind(point)) {
      leadFromYear = point.year + 1;
    }
  }

  return { winner, leadFromYear };
};
